/// <amd-module name="TFS/Admin/Views/ProfileSettings/OrganizationsView/Components/ConfirmationDialog" />
import React = require("react");

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { Link } from "VSSUI/Link";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";

import * as Resources from "TFS/Admin/Views/ProfileSettings/OrganizationsView/Resources";
import { Organization } from "TFS/Admin/Views/ProfileSettings/OrganizationsView/Contracts";
import { validateOrganizationName } from "TFS/Admin/Common/Util/NameValidator";
import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { localeFormat } from "VSS/Core/Util/String";
import { Observer } from "VSSUI/Observer";
import { ObservableValue } from "VSS/Core/Observable";

export interface IProfileDeleteSSHKeyDialogProps {
    onDismiss: () => void;
    onConfirm: (key: Organization, value?: string) => void;
    selectedOrg: Organization;
    isAadUser: boolean;
    isDeleted?: boolean;
}

export default class ConfirmationDialog extends VssComponent<IProfileDeleteSSHKeyDialogProps> {
    private readonly aadMembershipProvider = "ms.vss-admin-web.profile-organizations-aad-membership-data-provider";
    private readonly restoreAvailableProvider = "ms.vss-admin-web.profile-organizations-restore-available-data-provider";

    private _telemetryLogger = new AdminTelemetry(this.context.pageContext);
    private _title: string;
    private _action: string;
    private _confirmation = new ObservableValue<string>("");
    private _message = new ObservableValue<string>("");
    private _newName = new ObservableValue<string>("");
    private _isNameInvalid = new ObservableValue<boolean>(false);
    private _nameMessage = new ObservableValue<string>("");

    constructor(props: IProfileDeleteSSHKeyDialogProps, context: IVssComponentContext) {
        super(props, context);
        const { isDeleted } = this.props;
        this._title = !isDeleted ? Resources.LeaveTitle : Resources.RestoreTitle;
        this._confirmation.value = !isDeleted ? Resources.LeaveOrg : Resources.RestoreOrg;
        this._action = !isDeleted ? Resources.Leave : Resources.Restore;
    }

    public componentDidMount() {
        !!this.props.isDeleted ? this.initRestoreOrg() : this.initLeaveOrg();
    }

    public render() {
        const { selectedOrg, onConfirm, onDismiss, isDeleted } = this.props;
        return (
            <Observer disabled={this._isNameInvalid}>
                {(props: { disabled: boolean }) => (
                    <Dialog
                        titleProps={{ text: this._title }}
                        onDismiss={onDismiss}
                        footerButtonProps={[
                            {
                                text: Resources.Cancel,
                                onClick: onDismiss
                            },
                            {
                                danger: !isDeleted,
                                primary: !!isDeleted,
                                text: this._action,
                                disabled: props.disabled,
                                onClick: () => onConfirm(this.props.selectedOrg, this._newName.value)
                            }
                        ]}
                    >
                        <Observer text={this._confirmation}>
                            {(props: { text: string }) =>
                                <FormatComponent format={props.text}>
                                    <span className="font-weight-heavy">{selectedOrg.name}</span>
                                </FormatComponent>
                            }
                        </Observer>
                        <Observer content={this._message}>
                            {(props: { content: string }) => props.content}
                        </Observer>
                    </Dialog>
                )}
            </Observer>
        );
    }

    private initLeaveOrg = async () => {
        const telemetryLogger = this._startActionCapture("Fetch AAD membership");
        const { pageContext } = this.context;
        this._message.value = Resources.LeaveGenericMessage;
        try {
            if (!this.props.isAadUser) {
                return;
            }
            const service = pageContext.getService<IVssContributionService>("IVssContributionService");
            const groups = await service.getDataAsync<{ [id: string]: any }>(this.aadMembershipProvider, undefined, true);
            this._message.value = this.getLeaveAadMessage(groups);
        } catch (error) {
            telemetryLogger.captureError(error);
        }
    }

    private initRestoreOrg = async () => {
        const telemetryLogger = this._startActionCapture("Fetch deleted name availability");
        const { pageContext } = this.context;
        const accountId = this.props.selectedOrg.id;
        try {
            const service = pageContext.getService<IVssContributionService>("IVssContributionService");
            const canRestore = !!await service.getDataAsync<boolean>(this.restoreAvailableProvider, { accountId }, true);
            this._isNameInvalid.value = !canRestore;
            this._confirmation.value = canRestore ? Resources.RestoreOrg : Resources.RenameOrg;
            this._message.value = this.getRestoreContent(canRestore);
        } catch (error) {
            telemetryLogger.captureError(error);
        }
    }

    private _onNameChange = (ev: any, value: string) => {
        this._isNameInvalid.value = value == "" || value == this.props.selectedOrg.name || !validateOrganizationName(value);
        this._nameMessage.value = this._isNameInvalid.value ? localeFormat(Resources.InvalidName, value) : ""; 
        this._newName.value = value;
    };

    private getLeaveAadMessage(groups: any) {
        const hasGroups = !!groups.total;
        return <div>
            <p>
                {hasGroups ? localeFormat(Resources.LeaveAadMessage, groups.total) : Resources.LeaveGenericAadMessage}{". "}
                {this.getLearnMoreLink(Resources.LeaveLink)}
            </p>
            {hasGroups && <p>{Resources.LeaveGroupsTitle} {groups.names}</p>}
        </div> as unknown as string;
    }

    private getRestoreContent(canRestore: boolean){
        return (canRestore ? <div><p>{this.getLearnMoreLink(Resources.RestoreLink)}</p></div>
            : <div>
                {this.getLearnMoreLink(Resources.RenameOrg)}
                <p>{Resources.RenameMessage} {this.getLearnMoreLink(Resources.RenameMessageLink)}</p>
                <FormItem
                    className="fontWeightSemiBold"
                    label={Resources.NewName}
                    error={this._isNameInvalid}
                    message={this._nameMessage}
                >
                    <TextField
                        aria-label={Resources.NewNameAria}
                        required={true}
                        value={this._newName}
                        onChange={this._onNameChange}
                    />
                </FormItem>
            </div>) as unknown as string;
    }

    private getLearnMoreLink(link: string) {
        return <Link href={link} target="_blank" rel="noopener noreferrer" ariaLabel={Resources.LearnMore}>
            {Resources.LearnMore}
        </Link>
    }

    private _startActionCapture(action: string, properties: { [key: string]: any } = {}): AdminFeatureTelemetryLogger {
        return this._telemetryLogger.startActionCapture(Feature.ProfileOrganizations, Feature.ProfileOrganizations, action, properties);
    }
}
